// SC-1322/SC-1323: media-chrome writes its own aria-labels and tooltips (they replace any aria-label
// set in the template), so the portal player registers them for all 10 portal
// locales. Only the keys of the controls that player renders are listed; other keys fall back to the
// dictionary media-chrome or the podcast player already ships, then to English.
// Registration happens in registerMediaChromeLocale(), never at import time: every page module is
// loaded eagerly (app.js), and the podcast player's own nl/de/es labels must stay as they are.
import { addTranslation, setLanguage } from "media-chrome/utils/i18n.js"
import { En } from "media-chrome/lang/en.js"
import { De } from "media-chrome/lang/de.js"
import { Es } from "media-chrome/lang/es.js"
import { Fr } from "media-chrome/lang/fr.js"
import { Pt } from "media-chrome/lang/pt.js"
import { mediaChromeNl } from "./mediaChromeNl.js"
import { resolveLocale } from "./i18n.js"

export const MEDIA_CHROME_KEYS = [
    "Play", "Pause", "play", "pause",
    "Seek backward", "Seek forward", "seek back {seekOffset} seconds", "seek forward {seekOffset} seconds",
    "seek", "{currentTime} of {totalTime}", "playback time", "video not loaded, unknown time.",
    "Playback rate", "Playback rate {playbackRate}",
    "Enter fullscreen mode", "Exit fullscreen mode", "enter fullscreen mode", "exit fullscreen mode",
    "chapter: {chapterName}", "audio player", "video player", "media loading", "{time} remaining",
    "hour", "hours", "minute", "minutes", "second", "seconds",
]

const SHIPPED = { en: En, nl: mediaChromeNl, de: De, es: Es, fr: Fr, pt: Pt }

export const mediaChromeMessages = {
    en: Object.fromEntries(MEDIA_CHROME_KEYS.map((key) => [key, En[key]])),
    nl: {
        Play: "Afspelen", Pause: "Pauzeren", play: "afspelen", pause: "pauzeren",
        "Seek backward": "Terugspoelen", "Seek forward": "Vooruitspoelen",
        "seek back {seekOffset} seconds": "{seekOffset} seconden terug", "seek forward {seekOffset} seconds": "{seekOffset} seconden vooruit",
        seek: "positie", "{currentTime} of {totalTime}": "{currentTime} van {totalTime}", "playback time": "afspeeltijd",
        "video not loaded, unknown time.": "video niet geladen, tijd onbekend.",
        "Playback rate": "Afspeelsnelheid", "Playback rate {playbackRate}": "Afspeelsnelheid {playbackRate}",
        "Enter fullscreen mode": "Volledig scherm", "Exit fullscreen mode": "Volledig scherm sluiten",
        "enter fullscreen mode": "volledig scherm", "exit fullscreen mode": "volledig scherm sluiten",
        "chapter: {chapterName}": "hoofdstuk: {chapterName}", "audio player": "audiospeler", "video player": "videospeler",
        "media loading": "media wordt geladen", "{time} remaining": "nog {time}",
        hour: "uur", hours: "uur", minute: "minuut", minutes: "minuten", second: "seconde", seconds: "seconden",
    },
    de: {
        Play: "Abspielen", Pause: "Pause", play: "abspielen", pause: "pausieren",
        "Seek backward": "Zurückspulen", "Seek forward": "Vorspulen",
        "seek back {seekOffset} seconds": "{seekOffset} Sekunden zurück", "seek forward {seekOffset} seconds": "{seekOffset} Sekunden vor",
        seek: "Position", "{currentTime} of {totalTime}": "{currentTime} von {totalTime}", "playback time": "Wiedergabezeit",
        "video not loaded, unknown time.": "Video nicht geladen, Zeit unbekannt.",
        "Playback rate": "Wiedergabegeschwindigkeit", "Playback rate {playbackRate}": "Wiedergabegeschwindigkeit {playbackRate}",
        "Enter fullscreen mode": "Vollbild", "Exit fullscreen mode": "Vollbild beenden",
        "enter fullscreen mode": "Vollbild", "exit fullscreen mode": "Vollbild beenden",
        "chapter: {chapterName}": "Kapitel: {chapterName}", "audio player": "Audioplayer", "video player": "Videoplayer",
        "media loading": "Medien werden geladen", "{time} remaining": "noch {time}",
        hour: "Stunde", hours: "Stunden", minute: "Minute", minutes: "Minuten", second: "Sekunde", seconds: "Sekunden",
    },
    da: {
        Play: "Afspil", Pause: "Pause", play: "afspil", pause: "pause",
        "Seek backward": "Spol tilbage", "Seek forward": "Spol frem",
        "seek back {seekOffset} seconds": "{seekOffset} sekunder tilbage", "seek forward {seekOffset} seconds": "{seekOffset} sekunder frem",
        seek: "position", "{currentTime} of {totalTime}": "{currentTime} af {totalTime}", "playback time": "afspilningstid",
        "video not loaded, unknown time.": "video ikke indlæst, ukendt tid.",
        "Playback rate": "Afspilningshastighed", "Playback rate {playbackRate}": "Afspilningshastighed {playbackRate}",
        "Enter fullscreen mode": "Fuld skærm", "Exit fullscreen mode": "Afslut fuld skærm",
        "enter fullscreen mode": "fuld skærm", "exit fullscreen mode": "afslut fuld skærm",
        "chapter: {chapterName}": "kapitel: {chapterName}", "audio player": "lydafspiller", "video player": "videoafspiller",
        "media loading": "mediet indlæses", "{time} remaining": "{time} tilbage",
        hour: "time", hours: "timer", minute: "minut", minutes: "minutter", second: "sekund", seconds: "sekunder",
    },
    es: {
        Play: "Reproducir", Pause: "Pausar", play: "reproducir", pause: "pausar",
        "Seek backward": "Retroceder", "Seek forward": "Avanzar",
        "seek back {seekOffset} seconds": "retroceder {seekOffset} segundos", "seek forward {seekOffset} seconds": "avanzar {seekOffset} segundos",
        seek: "posición", "{currentTime} of {totalTime}": "{currentTime} de {totalTime}", "playback time": "tiempo de reproducción",
        "video not loaded, unknown time.": "vídeo no cargado, tiempo desconocido.",
        "Playback rate": "Velocidad de reproducción", "Playback rate {playbackRate}": "Velocidad de reproducción {playbackRate}",
        "Enter fullscreen mode": "Pantalla completa", "Exit fullscreen mode": "Salir de pantalla completa",
        "enter fullscreen mode": "pantalla completa", "exit fullscreen mode": "salir de pantalla completa",
        "chapter: {chapterName}": "capítulo: {chapterName}", "audio player": "reproductor de audio", "video player": "reproductor de vídeo",
        "media loading": "cargando contenido", "{time} remaining": "quedan {time}",
        hour: "hora", hours: "horas", minute: "minuto", minutes: "minutos", second: "segundo", seconds: "segundos",
    },
    fr: {
        Play: "Lire", Pause: "Pause", play: "lire", pause: "mettre en pause",
        "Seek backward": "Reculer", "Seek forward": "Avancer",
        "seek back {seekOffset} seconds": "reculer de {seekOffset} secondes", "seek forward {seekOffset} seconds": "avancer de {seekOffset} secondes",
        seek: "position", "{currentTime} of {totalTime}": "{currentTime} sur {totalTime}", "playback time": "temps de lecture",
        "video not loaded, unknown time.": "vidéo non chargée, durée inconnue.",
        "Playback rate": "Vitesse de lecture", "Playback rate {playbackRate}": "Vitesse de lecture {playbackRate}",
        "Enter fullscreen mode": "Plein écran", "Exit fullscreen mode": "Quitter le plein écran",
        "enter fullscreen mode": "plein écran", "exit fullscreen mode": "quitter le plein écran",
        "chapter: {chapterName}": "chapitre : {chapterName}", "audio player": "lecteur audio", "video player": "lecteur vidéo",
        "media loading": "chargement du média", "{time} remaining": "{time} restantes",
        hour: "heure", hours: "heures", minute: "minute", minutes: "minutes", second: "seconde", seconds: "secondes",
    },
    it: {
        Play: "Riproduci", Pause: "Pausa", play: "riproduci", pause: "metti in pausa",
        "Seek backward": "Indietro", "Seek forward": "Avanti",
        "seek back {seekOffset} seconds": "indietro di {seekOffset} secondi", "seek forward {seekOffset} seconds": "avanti di {seekOffset} secondi",
        seek: "posizione", "{currentTime} of {totalTime}": "{currentTime} di {totalTime}", "playback time": "tempo di riproduzione",
        "video not loaded, unknown time.": "video non caricato, durata sconosciuta.",
        "Playback rate": "Velocità di riproduzione", "Playback rate {playbackRate}": "Velocità di riproduzione {playbackRate}",
        "Enter fullscreen mode": "Schermo intero", "Exit fullscreen mode": "Esci da schermo intero",
        "enter fullscreen mode": "schermo intero", "exit fullscreen mode": "esci da schermo intero",
        "chapter: {chapterName}": "capitolo: {chapterName}", "audio player": "lettore audio", "video player": "lettore video",
        "media loading": "caricamento in corso", "{time} remaining": "{time} rimanenti",
        hour: "ora", hours: "ore", minute: "minuto", minutes: "minuti", second: "secondo", seconds: "secondi",
    },
    pl: {
        Play: "Odtwórz", Pause: "Wstrzymaj", play: "odtwórz", pause: "wstrzymaj",
        "Seek backward": "Przewiń do tyłu", "Seek forward": "Przewiń do przodu",
        "seek back {seekOffset} seconds": "przewiń {seekOffset} s do tyłu", "seek forward {seekOffset} seconds": "przewiń {seekOffset} s do przodu",
        seek: "pozycja", "{currentTime} of {totalTime}": "{currentTime} z {totalTime}", "playback time": "czas odtwarzania",
        "video not loaded, unknown time.": "wideo nie zostało wczytane, czas nieznany.",
        "Playback rate": "Szybkość odtwarzania", "Playback rate {playbackRate}": "Szybkość odtwarzania {playbackRate}",
        "Enter fullscreen mode": "Pełny ekran", "Exit fullscreen mode": "Zamknij pełny ekran",
        "enter fullscreen mode": "pełny ekran", "exit fullscreen mode": "zamknij pełny ekran",
        "chapter: {chapterName}": "rozdział: {chapterName}", "audio player": "odtwarzacz audio", "video player": "odtwarzacz wideo",
        "media loading": "wczytywanie multimediów", "{time} remaining": "pozostało {time}",
        hour: "godzina", hours: "godz.", minute: "minuta", minutes: "min", second: "sekunda", seconds: "s",
    },
    pt: {
        Play: "Reproduzir", Pause: "Pausa", play: "reproduzir", pause: "pausar",
        "Seek backward": "Recuar", "Seek forward": "Avançar",
        "seek back {seekOffset} seconds": "recuar {seekOffset} segundos", "seek forward {seekOffset} seconds": "avançar {seekOffset} segundos",
        seek: "posição", "{currentTime} of {totalTime}": "{currentTime} de {totalTime}", "playback time": "tempo de reprodução",
        "video not loaded, unknown time.": "vídeo não carregado, tempo desconhecido.",
        "Playback rate": "Velocidade de reprodução", "Playback rate {playbackRate}": "Velocidade de reprodução {playbackRate}",
        "Enter fullscreen mode": "Ecrã inteiro", "Exit fullscreen mode": "Sair do ecrã inteiro",
        "enter fullscreen mode": "ecrã inteiro", "exit fullscreen mode": "sair do ecrã inteiro",
        "chapter: {chapterName}": "capítulo: {chapterName}", "audio player": "leitor de áudio", "video player": "leitor de vídeo",
        "media loading": "a carregar multimédia", "{time} remaining": "faltam {time}",
        hour: "hora", hours: "horas", minute: "minuto", minutes: "minutos", second: "segundo", seconds: "segundos",
    },
    sv: {
        Play: "Spela", Pause: "Pausa", play: "spela", pause: "pausa",
        "Seek backward": "Spola bakåt", "Seek forward": "Spola framåt",
        "seek back {seekOffset} seconds": "{seekOffset} sekunder bakåt", "seek forward {seekOffset} seconds": "{seekOffset} sekunder framåt",
        seek: "position", "{currentTime} of {totalTime}": "{currentTime} av {totalTime}", "playback time": "uppspelningstid",
        "video not loaded, unknown time.": "videon har inte laddats, okänd tid.",
        "Playback rate": "Uppspelningshastighet", "Playback rate {playbackRate}": "Uppspelningshastighet {playbackRate}",
        "Enter fullscreen mode": "Helskärm", "Exit fullscreen mode": "Avsluta helskärm",
        "enter fullscreen mode": "helskärm", "exit fullscreen mode": "avsluta helskärm",
        "chapter: {chapterName}": "kapitel: {chapterName}", "audio player": "ljudspelare", "video player": "videospelare",
        "media loading": "media laddas", "{time} remaining": "{time} kvar",
        hour: "timme", hours: "timmar", minute: "minut", minutes: "minuter", second: "sekund", seconds: "sekunder",
    },
}

export function mediaChromeDictionary(locale) {
    const code = resolveLocale(locale)

    return { ...(SHIPPED[code] ?? {}), ...mediaChromeMessages[code] }
}

/**
 * Call once from the page's setup, before any media-chrome element is created (labels are
 * rendered in each element's constructor). Returns the code to put on <media-controller lang>.
 */
export function registerMediaChromeLocale(locale) {
    const code = resolveLocale(locale)

    addTranslation(code, mediaChromeDictionary(code))
    setLanguage(code)

    return code
}
